import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Button,
    cn,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Label,
    Textarea,
} from "@athena/ui";
import { useMarkLostContact } from "@/hooks/useOutreachStatus";
import { fullName, STATUS_REASON_MAX_LENGTH, type ClientRecord } from "../helpers/client";

interface MarkLostContactProps {
    client: ClientRecord;
    /** For the trigger, so it can match the buttons beside it: the queue's pills, the profile's blocks. */
    className?: string;
}

/**
 * "Mark lost contact", offered once a client has gone six check-in intervals without being reached.
 *
 * The app only suggests it; the coordinator decides, and says why. The reason is required — whoever
 * picks this client up later needs the grounds — and the dialog says what changes (they leave the
 * queue) and what does not (their record), per "Destructive and batch actions" in `.claude/rules/ui.md`.
 */
export function MarkLostContact({ client, className }: MarkLostContactProps) {
    const { t } = useTranslation();
    const ids = useId();
    const markLostContact = useMarkLostContact();
    const [
        open,
        setOpen,
    ] = useState<boolean>(false);
    const [
        reason,
        setReason,
    ] = useState<string>("");
    const [
        saving,
        setSaving,
    ] = useState<boolean>(false);

    const name = fullName(client);

    const changeOpen = (next: boolean) => {
        if (saving) return;
        if (!next) setReason("");
        setOpen(next);
    };

    const confirm = async () => {
        setSaving(true);
        try {
            await markLostContact(client.id, name, reason.trim());
            setOpen(false);
            setReason("");
        } catch {
            // The hook has said what went wrong; the dialog stays up with the reason as typed.
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <Button variant="outline" className={cn("w-auto", className)} onClick={() => setOpen(true)}>
                {t("admin.alumni.lostContact.mark")}
            </Button>
            <Dialog open={open} onOpenChange={changeOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{t("admin.alumni.lostContact.title", { name })}</DialogTitle>
                        <DialogDescription>
                            {t("admin.alumni.lostContact.description", { firstName: client.firstName })}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex flex-col gap-2">
                        <Label htmlFor={`${ids}-reason`}>{t("admin.alumni.lostContact.reasonLabel")}</Label>
                        <Textarea
                            id={`${ids}-reason`}
                            value={reason}
                            maxLength={STATUS_REASON_MAX_LENGTH}
                            onChange={(event) => setReason(event.target.value)}
                            placeholder={t("admin.alumni.lostContact.reasonPlaceholder")}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => changeOpen(false)} disabled={saving}>
                            {t("common.actions.cancel")}
                        </Button>
                        <Button variant="destructive" loading={saving} disabled={!reason.trim()} onClick={confirm}>
                            {t("admin.alumni.lostContact.confirm")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
